import { useEffect, useState } from "react";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { ApiError } from "@repo/api-client";
import {
  activateTrial,
  activeTrial,
  adjustTrialGrant,
  endTrialEarly,
  extendTrial,
  requestTrial,
  type TrialView,
} from "./opsApi";
import { opsButton } from "./opsStyles";

// The full flag vocabulary. NOTE: a trial can only grant flags in the cheapest plan (R3) — the server
// 422s the rest. Non-plan flags (e.g. FLOOR_PLAN) belong on the feature-override panel above, not here.
const FLAGS = [
  "CRM_NOTES_TAGS",
  "FLOOR_PLAN",
  "WAITLIST",
  "SMS",
  "WHATSAPP",
  "MULTI_SITE",
  "MEDIA",
  "CUSTOM_DOMAIN",
] as const;

const DEFAULT_FLAGS = ["CRM_NOTES_TAGS"];

/**
 * The operator sales-trial panel on the per-tenant `/ops` record (sales-trials.md, R1). Operators
 * (`platform-operator`) request/activate/adjust/extend/end a trial on an account; every command carries a
 * MANDATORY reason (R2 — the server 400s a blank one) and — for activate/adjust/extend/end — the CAS
 * expected version (OP-4, a 409 surfaces the stale-record notice). There is NO self-serve path: this
 * surface exists only inside `/ops`, gated by the operator realm role.
 */
export function TrialPanel({ tenantId }: { tenantId: string }) {
  const { t } = useTranslation();
  const [trial, setTrial] = useState<TrialView | null>(null);
  const [reason, setReason] = useState("");
  const [days, setDays] = useState(14);
  const [flags, setFlags] = useState<string[]>(DEFAULT_FLAGS);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    activeTrial(tenantId)
      .then((v) => {
        if (!active) return;
        setTrial(v);
        if (v) setFlags(v.grantedFlags);
      })
      .catch(() => active && setNotice(t("ops.trial.error")));
    return () => {
      active = false;
    };
  }, [tenantId, t]);

  async function reload() {
    const v = await activeTrial(tenantId);
    setTrial(v);
    if (v) setFlags(v.grantedFlags);
  }

  function toggleFlag(flag: string) {
    setFlags((prev) => (prev.includes(flag) ? prev.filter((f) => f !== flag) : [...prev, flag]));
  }

  function requireReason(): string | null {
    const r = reason.trim();
    if (!r) {
      setNotice(t("ops.trial.reasonRequired"));
      return null;
    }
    setNotice(null);
    return r;
  }

  async function run(fn: (reason: string) => Promise<unknown>, ok: string) {
    const r = requireReason();
    if (!r) return;
    try {
      await fn(r);
      setReason("");
      setNotice(t(ok));
      await reload();
    } catch (e) {
      setNotice(errorNotice(e, t));
    }
  }

  const onRequest = () => run((r) => requestTrial(tenantId, flags, days, r), "ops.trial.requested");
  const onActivate = () =>
    trial &&
    run((r) => activateTrial(tenantId, trial.trialId, r, trial.version), "ops.trial.activated");
  const onAdjust = () =>
    trial &&
    run(
      (r) => adjustTrialGrant(tenantId, trial.trialId, flags, r, trial.version),
      "ops.trial.adjusted",
    );
  const onExtend = () =>
    trial &&
    run(
      (r) => extendTrial(tenantId, trial.trialId, trial.endsAtSec + 7 * 86_400, r, trial.version),
      "ops.trial.extended",
    );
  const onEndEarly = () =>
    trial &&
    run((r) => endTrialEarly(tenantId, trial.trialId, r, trial.version), "ops.trial.endedEarly");

  const showPicker = !trial || trial.state === "ACTIVE";

  return (
    <section
      data-testid="ops-trial-panel"
      className={css({
        mt: "5",
        p: "3",
        borderWidth: "1px",
        borderColor: "border.default",
        rounded: "md",
      })}
    >
      <h2 className={css({ fontWeight: "semibold", mb: "2" })}>{t("ops.trial.heading")}</h2>

      {trial ? (
        <p
          data-testid="ops-trial-state"
          className={css({ color: "fg.muted", mb: "2", fontSize: "sm" })}
        >
          {t("ops.trial.state", { state: trial.state })}
          {trial.state === "REQUESTED"
            ? ` · ${t("ops.trial.dueAt", { due: new Date(trial.activationDueAtSec * 1000).toLocaleString() })}`
            : ` · ${t("ops.trial.endsAt", { ends: new Date(trial.endsAtSec * 1000).toLocaleDateString() })}`}
        </p>
      ) : (
        <p className={css({ color: "fg.muted", mb: "2", fontSize: "sm" })}>{t("ops.trial.none")}</p>
      )}

      {showPicker && (
        <fieldset className={css({ mb: "2" })}>
          <legend className={css({ fontWeight: "medium", mb: "1", fontSize: "sm" })}>
            {t("ops.trial.flags")}
          </legend>
          <div className={css({ display: "flex", gap: "3", flexWrap: "wrap" })}>
            {FLAGS.map((flag) => (
              <label key={flag} className={css({ display: "flex", gap: "1", fontSize: "sm" })}>
                <input
                  type="checkbox"
                  checked={flags.includes(flag)}
                  onChange={() => toggleFlag(flag)}
                />
                {flag}
              </label>
            ))}
          </div>
          <p className={css({ color: "fg.muted", fontSize: "xs", mt: "1" })}>
            {t("ops.trial.flagsHint")}
          </p>
        </fieldset>
      )}

      <label className={css({ display: "block", mb: "2" })}>
        <span className={css({ display: "block", fontWeight: "medium", mb: "1" })}>
          {t("ops.trial.reason")}
        </span>
        <textarea
          data-testid="ops-trial-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          className={css({
            w: "full",
            p: "2",
            borderWidth: "1px",
            borderColor: "border.default",
            rounded: "md",
          })}
        />
      </label>
      {notice && (
        <output className={css({ display: "block", mb: "2", color: "fg.muted" })}>{notice}</output>
      )}

      <div className={css({ display: "flex", gap: "2", flexWrap: "wrap" })}>
        {!trial && (
          <>
            <label>
              {t("ops.trial.durationDays")}
              <input
                type="number"
                data-testid="ops-trial-days"
                value={days}
                min={1}
                onChange={(e) => setDays(Number(e.target.value))}
                className={css({
                  ml: "1",
                  w: "16",
                  p: "1",
                  borderWidth: "1px",
                  borderColor: "border.default",
                  rounded: "sm",
                })}
              />
            </label>
            <button
              type="button"
              className={opsButton}
              data-testid="ops-trial-request"
              onClick={onRequest}
            >
              {t("ops.trial.request")}
            </button>
          </>
        )}
        {trial?.state === "REQUESTED" && (
          <button
            type="button"
            className={opsButton}
            data-testid="ops-trial-activate"
            onClick={onActivate}
          >
            {t("ops.trial.activate")}
          </button>
        )}
        {trial?.state === "ACTIVE" && (
          <>
            <button
              type="button"
              className={opsButton}
              data-testid="ops-trial-adjust"
              onClick={onAdjust}
            >
              {t("ops.trial.adjust")}
            </button>
            <button
              type="button"
              className={opsButton}
              data-testid="ops-trial-extend"
              onClick={onExtend}
            >
              {t("ops.trial.extend")}
            </button>
            <button
              type="button"
              className={opsButton}
              data-testid="ops-trial-end-early"
              onClick={onEndEarly}
            >
              {t("ops.trial.endEarly")}
            </button>
          </>
        )}
      </div>
    </section>
  );
}

type Translate = (key: string, opts?: Record<string, unknown>) => string;

function errorNotice(e: unknown, t: Translate): string {
  if (e instanceof ApiError && e.status === 409) return t("ops.trial.stale");
  if (e instanceof ApiError && e.status === 422) return t("ops.trial.rejected");
  return t("ops.trial.error");
}
